import React, { useMemo } from 'react';
import CameraswitchRoundedIcon from '@mui/icons-material/CameraswitchRounded';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import { Box, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AcceptanceDemoButton,
  AcceptanceDemoIconContainer,
  AcceptanceDemoPageShell,
  AcceptanceDemoPanel,
  acceptanceDemoTokens,
} from '../components/overview/acceptanceDemoDesignSystem';
import {
  DEFAULT_WEB_SUMMIT_SCENARIO_ID,
  buildAcceptanceImpactPath,
  buildAcceptanceScenarioProofPath,
  buildAcceptanceScenarioReviewPath,
  getWebSummitDemoScenarioById,
} from './demo/webSummitDemoScenarios';

const StatusTile: React.FC<{
  title: string;
  detail: string;
  accent: 'blue' | 'orange';
  icon: React.ReactNode;
}> = ({ title, detail, accent, icon }) => (
  <AcceptanceDemoPanel
    tone={accent}
    sx={{
      p: { xs: 1.35, sm: 1.55 },
      minHeight: 160,
    }}
  >
    <Stack spacing={1}>
      <AcceptanceDemoIconContainer accent={accent} sx={{ width: 38, height: 38 }}>
        {icon}
      </AcceptanceDemoIconContainer>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.5 }}>
        {detail}
      </Typography>
    </Stack>
  </AcceptanceDemoPanel>
);

const AcceptanceResolutionWalkthrough: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { scenarioId } = useParams<{ scenarioId: string }>();

  const scenario = useMemo(
    () => getWebSummitDemoScenarioById(scenarioId || DEFAULT_WEB_SUMMIT_SCENARIO_ID),
    [scenarioId],
  );
  const reviewPath = useMemo(
    () => buildAcceptanceScenarioReviewPath(scenario.id, location.search),
    [location.search, scenario.id],
  );
  const proofPath = useMemo(
    () => buildAcceptanceScenarioProofPath(scenario.id, location.search),
    [location.search, scenario.id],
  );
  const impactPath = useMemo(() => buildAcceptanceImpactPath(location.search), [location.search]);

  return (
    <AcceptanceDemoPageShell>
      <AcceptanceDemoPanel tone="hero" sx={{ p: { xs: 2.2, md: 3 }, minHeight: { md: 720 } }}>
        <Stack spacing={{ xs: 2.1, md: 2.5 }} sx={{ maxWidth: 1040, mx: 'auto' }}>
          <Box sx={{ maxWidth: 760 }}>
            <Typography
              variant="overline"
              sx={{ color: acceptanceDemoTokens.colors.orangeSoft, fontWeight: 900, letterSpacing: 1.35 }}
            >
              LIVE RTSP DEMO
            </Typography>
            <Typography
              variant="h2"
              sx={{
                mt: 1.15,
                fontWeight: 900,
                letterSpacing: -0.04,
                lineHeight: 1.02,
                fontSize: { xs: '2.2rem', md: '3.15rem' },
              }}
            >
              Pedestrian/Forklift Safety Demo
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 1,
                maxWidth: 680,
                color: acceptanceDemoTokens.colors.textSecondary,
                fontWeight: 500,
                lineHeight: 1.48,
                fontSize: { xs: '1rem', md: '1.05rem' },
              }}
            >
              A local RTSP camera feed detects pedestrian activity in a forklift operating area and turns it into a
              reviewable danger-zone observation.
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1.1, color: acceptanceDemoTokens.colors.textPrimary, fontWeight: 700, letterSpacing: 0.15 }}
            >
              Local RTSP feed · Forklift operating area · Pedestrian activity · Looping demo video
            </Typography>
          </Box>

          <AcceptanceDemoPanel tone="subtle" sx={{ p: { xs: 1.25, sm: 1.45 }, maxWidth: 880 }}>
            <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.55 }}>
              This demo uses a looped video feed, so outcome reduction is not measured here. The demo shows
              detection, review, action logging, and proof readiness.
            </Typography>
          </AcceptanceDemoPanel>

          <AcceptanceDemoPanel tone="subtle" sx={{ p: { xs: 1.5, sm: 1.8 } }}>
            <Stack spacing={1.2}>
              <Typography variant="overline" sx={{ color: acceptanceDemoTokens.colors.blueSoft, fontWeight: 800, letterSpacing: 1 }}>
                Demo flow
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  letterSpacing: -0.03,
                  lineHeight: 1.25,
                  textAlign: { xs: 'left', md: 'center' },
                }}
              >
                Camera Feed → Risk Detected → Review Case → Log Action → Prepare Proof
              </Typography>
            </Stack>
          </AcceptanceDemoPanel>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 1.2,
            }}
          >
            <StatusTile
              title="Camera feed active"
              detail="Local RTSP stream is available for analysis."
              accent="blue"
              icon={<CameraswitchRoundedIcon fontSize="small" />}
            />
            <StatusTile
              title="Safety case created"
              detail="Pedestrian activity is converted into a reviewable incident."
              accent="orange"
              icon={<RateReviewRoundedIcon fontSize="small" />}
            />
            <StatusTile
              title="Proof path ready"
              detail="The case can be reviewed, actioned, and packaged for handoff."
              accent="blue"
              icon={<PlaylistAddCheckRoundedIcon fontSize="small" />}
            />
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" sx={{ pt: 0.5 }}>
            <AcceptanceDemoButton
              label="Open Review Workflow"
              onClick={() => navigate(reviewPath)}
              accent="orange"
            />
            <AcceptanceDemoButton
              label="View Proof Package"
              onClick={() => navigate(proofPath)}
              accent="blue"
              variant="outlined"
            />
            <AcceptanceDemoButton
              label="Back to Impact Scenarios"
              onClick={() => navigate(impactPath)}
              accent="neutral"
              variant="text"
            />
          </Stack>
        </Stack>
      </AcceptanceDemoPanel>
    </AcceptanceDemoPageShell>
  );
};

export default AcceptanceResolutionWalkthrough;
